import { useMemo } from 'react';
import { deleteDailyPuzzleById } from 'services/api/dailyPuzzlesApi';
import { NavLink } from 'react-router-dom';
import { DashboardRoutePath } from 'modules/dashboard/navigation';

import { Box, FlexContainer, Text } from '../../../../shared/Typhography';
import { TableComponent } from '../../../../shared/Components/Table/Table';
import { Button, Splash } from '../../../../shared/Components';
import DatePickerInput from '../../../../shared/Components/DatePickerInput';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';

import useDailyPuzzles from './hooks/useDailyPuzzles';
import { Icon } from './styles';
import { isAbleToMutateDailyPuzzle } from './utils/helpers';

const DailyPuzzlesScreen: React.FC = () => {
  const { data, isLoading, toggleDateSort, fetch } = useDailyPuzzles();

  const { showModal } = useGlobalModalContext();

  const dotDays = useMemo(() => (data?.length ? data.map((item) => item.date) : []), [data]);

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'date',
        Cell: (cell: any) => (
          <span style={{ marginLeft: 10 }}>
            {cell?.row?.original?.date?.toLocaleString('en-us', {
              month: 'short',
              year: 'numeric',
              day: 'numeric',
            }) || '/'}
          </span>
        ),
        width: '5%',
        disableSortBy: false,
        onHeaderClick: () => toggleDateSort(),
      },
      {
        Header: 'Puzzle ID',
        accessor: 'puzzleId',
        Cell: (cell: any) => (
          <NavLink
            style={{ color: '#000', textDecoration: 'none' }}
            to={`/${DashboardRoutePath.VIEW_PUZZLE}/${cell?.row?.original?.puzzleId}`}
          >
            {cell?.row?.original?.puzzleId}
          </NavLink>
        ),
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: 'Puzzle Icon',
        accessor: 'puzzleIcon',
        Cell: (cell: any) => <Icon src={cell?.row?.original?.iconUrl} />,
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: 'Puzzle Name',
        accessor: 'puzzleName',
        Cell: (cell: any) => <>{cell?.row?.original?.puzzleName}</>,
        disableSortBy: true,
        width: '10%',
      },
      {
        Header: '',
        accessor: 'actions',
        Cell: (cell: any) => (
          <>
            <Button
              variant="link"
              onClick={() => {
                showModal(MODAL_TYPES.CREATE_EDIT_DAILY_PUZZLE, {
                  editDailyPuzzleId: cell?.row?.original?.id,
                  onSaveCallback: fetch,
                  dotDays,
                });
              }}
              disabled={!isAbleToMutateDailyPuzzle(cell?.row?.original?.date)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              onClick={() => {
                showModal(MODAL_TYPES.DIALOG_MODAL, {
                  title: 'Delete Daily Puzzle?',
                  text: `Are you sure you want to delete this puzzle for ${
                    cell?.row?.original.date?.toLocaleString('en-us', {
                      month: 'short',
                      year: 'numeric',
                      day: 'numeric',
                    }) || '/'
                  }?`,
                  cancelButtonText: 'Cancel',
                  submitButtonText: 'Delete',
                  onSubmit: async () => {
                    await deleteDailyPuzzleById(cell?.row?.original?.id);
                    fetch();
                  },
                });
              }}
              disabled={!isAbleToMutateDailyPuzzle(cell?.row?.original?.date)}
            >
              Delete
            </Button>
          </>
        ),
        disableSortBy: true,
        width: '3%',
      },
    ],
    [toggleDateSort],
  );

  return (
    <>
      <Box margin="150px auto 0">
        <FlexContainer justifyContent="space-between">
          <DatePickerInput
            selected={null}
            onChange={() => {}}
            inline={true}
            label="Date Range"
            dotDays={dotDays}
            readOnlyCalendar={true}
          />
          <Button
            height={60}
            width={150}
            onClick={() =>
              showModal(MODAL_TYPES.CREATE_EDIT_DAILY_PUZZLE, {
                onSaveCallback: fetch,
                dotDays,
              })
            }
          >
            <span style={{ fontSize: '16px' }}>Create</span>
          </Button>
        </FlexContainer>
      </Box>
      <Box margin="50px auto 150px">
        {isLoading ? (
          <Splash margin="40px 0 0 0" position="relative" />
        ) : data?.length === 0 ? (
          <Text textAlign="center">No results...</Text>
        ) : (
          <TableComponent
            loadingMore={false}
            fetchMore={false}
            columns={columns}
            data={data.map((item, index) => ({ index: index + 1, ...item }))}
          />
        )}
      </Box>
    </>
  );
};

export default DailyPuzzlesScreen;
